import { PostEditingDetails } from "@/pages/Posts/PostEditor/PostEditor/postEditorConstants";
import { message } from "antd";
import { genericError } from "fixtures/globalConstants";
import { useCategories } from "hooks/useCategoryCRUD";
import useGetPostDetailsQuery from "hooks/useGetPostDetailsQuery";
import useThemes from "hooks/useThemes";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import { updateBusinessDetails } from "store/business/businessActions";
import { Post } from "store/posts/postConstants";
import { currentBusinessGetter } from "store/user/userSlice";
import useUpdatePostActions from "../../utils/useUpdatePostActions";

/**
 * Encapsulates the logic for fetching post details, managing local card state,
 * and handling actions like theme toggling and category updates within GenericPostCard.
 */
export const usePostCardLogic = (post: Post) => {
  const [isShowingInputs, setIsShowingInputs] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false); // Keep isDownloading state if used by menu logic passed out
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const { savePost } = useUpdatePostActions();

  const dispatch = useAppDispatch();
  const currentBusiness = useAppSelector(currentBusinessGetter);
  const { themes } = useThemes();
  const { categories, isLoadingCategories } = useCategories();

  // Fetch details, enable when card is flipped
  const {
    data: postDetailData,
    refetch,
    isLoading: isFetchingPostDetails,
  } = useGetPostDetailsQuery(post.id, {
    enabled: isShowingInputs,
    staleTime: 5 * 60 * 1000,
  });

  // Define postDetail based on fetched data or initial post
  const postDetail: PostEditingDetails = useMemo(() => {
    const fetched = postDetailData;
    return {
      mediaUrls: fetched?.media_urls || post.media_urls || [],
      template: fetched?.template || post.template || null,
      inputs: fetched?.inputs || post.inputs || undefined,
    };
  }, [postDetailData, post]);

  const toggleShowInputs = () => setIsShowingInputs(!isShowingInputs);

  const handleToggleTheme = async () => {
    if (!postDetail?.template?.theme) {
      message.warning("Post does not have an associated theme to toggle.");
      return;
    }

    const themeName = postDetail.template.theme;
    const enabledThemes = currentBusiness.brand?.themes || [];
    const isThemeEnabled = enabledThemes.includes(themeName);
    const themeDisplayName =
      themes?.find((t: any) => t.name === themeName)?.display_name || themeName;

    const updatedThemes = isThemeEnabled
      ? enabledThemes.filter((t: string) => t !== themeName)
      : [...enabledThemes, themeName];

    try {
      await dispatch(updateBusinessDetails({ themes: updatedThemes } as any));
      message.success(
        `Theme "${themeDisplayName}" ${isThemeEnabled ? "disabled" : "enable"}`
      );
    } catch (error) {
      console.error(
        `Failed to ${isThemeEnabled ? "disable" : "enable"} theme:`,
        error
      );
      genericError(error);
    }
  };

  // Placeholder function to handle category updates
  const handleUpdateCategory = async (categoryId: string | null) => {
    setIsUpdatingCategory(true);
    try {
      await savePost({
        post,
        updateBody: {
          // @ts-expect-error - Assume backend accepts category_id even if type is not updated
          category_id: categoryId,
        },
        showMessage: false,
      });
      // Optionally refetch post data if needed, though savePost might handle this via store updates
    } catch (error) {
      console.error("Failed to update category:", error);
      genericError(error); // Use existing generic error handler
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  return {
    isShowingInputs,
    isDownloading,
    isUpdatingCategory,
    isFetchingPostDetails,
    postDetail,
    categories,
    isLoadingCategories,
    toggleShowInputs,
    handleToggleTheme,
    handleUpdateCategory,
    refetchPostDetails: refetch,
    postDetailData, // Expose raw data if needed by menu
    setIsDownloading, // Expose setter if menu needs to control it
  };
};
